import supabase from '../supabase/supabase';
import { differenceInDays, parseISO, isBefore, isWithinInterval } from 'date-fns';

class StatementService {
  async getCustomerStatement(customerId, startDate, endDate) {
    try {
      // 1. Fetch Customer Details
      const { data: customer, error: custError } = await supabase
        .schema('accounting')
        .from('customers_20240522')
        .select('*')
        .eq('id', customerId)
        .single();

      if (custError) throw custError;

      // 2. Fetch Invoices for this customer (excluding Drafts)
      const { data: invoices, error: invError } = await supabase
        .schema('accounting')
        .from('invoices_20240522')
        .select('*')
        .eq('customer_id', customerId)
        .neq('status', 'Draft')
        .order('issue_date', { ascending: true });

      if (invError) throw invError;

      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const asOf = new Date(); // Aging is calculated as of today

      let balanceForward = 0;
      const activity = [];
      const aging = { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '90+': 0, total: 0 };

      // 3. Process Invoices
      invoices.forEach(inv => {
        const issueDate = parseISO(inv.issue_date);
        const dueDate = parseISO(inv.due_date || inv.issue_date);
        const amount = Number(inv.amount);
        const isPaid = inv.status === 'Paid';
        const balance = isPaid ? 0 : amount; // Assuming fully paid if 'Paid', else full amount

        // Calculate Balance Forward (Invoices issued before the start date)
        if (isBefore(issueDate, start)) {
          balanceForward += balance;
        }

        // Calculate Period Activity (Invoices issued within the date range)
        if (isWithinInterval(issueDate, { start, end })) {
          activity.push({
            id: inv.id,
            date: inv.issue_date,
            type: 'Invoice',
            ref: inv.invoice_number,
            amount: amount,
            balance: balance
          });

          // If the invoice is paid, simulate the payment line in the activity
          if (isPaid) {
            activity.push({
              id: `${inv.id}_payment`,
              date: inv.issue_date, // Using issue date as proxy for payment date in this simplified model
              type: 'Payment',
              ref: `PAY-${inv.invoice_number}`,
              amount: -amount,
              balance: 0
            });
          }
        }

        // Calculate Aging (Based on unpaid invoices)
        if (!isPaid && inv.status !== 'Void') {
          const daysOverdue = differenceInDays(asOf, dueDate);
          aging.total += balance;
          
          if (daysOverdue <= 0) aging.current += balance;
          else if (daysOverdue <= 30) aging['1-30'] += balance;
          else if (daysOverdue <= 60) aging['31-60'] += balance;
          else if (daysOverdue <= 90) aging['61-90'] += balance;
          else aging['90+'] += balance;
        }
      });

      // Sort activity chronologically
      activity.sort((a, b) => new Date(a.date) - new Date(b.date));

      // 4. Build Final Activity Array with Running Balance
      let runningBalance = balanceForward;
      const finalActivity = [
        {
          id: 'balance-forward',
          date: startDate,
          type: 'Balance Forward',
          ref: '',
          amount: balanceForward,
          balance: balanceForward
        }
      ];

      activity.forEach(act => {
        runningBalance += act.amount;
        finalActivity.push({ ...act, balance: runningBalance });
      });

      return {
        customer,
        activity: finalActivity,
        aging,
        summary: {
          balanceForward,
          endingBalance: runningBalance
        }
      };
    } catch (error) {
      console.error("Statement generation failed:", error);
      throw error;
    }
  }
}

export const statementService = new StatementService();